import { defa }           from 'defa-object';
import ConfigView         from 'scripts/defa/basic/view-config-view';
import SuccessView        from 'scripts/defa/basic/view-success-view';
import PickupTime         from 'scripts/defa/control/pickup-times/model-pickuptime';
import SettingsAutomotive from 'scripts/defa/settings-automotive/model-settings-automotive';
import Subscription       from 'scripts/defa/subscriptions/model-subscription';
import IasZone            from 'scripts/defa/unit-feature-sets/endpoints/wiredAlarmInput/model-iasZone';
import Unit               from 'scripts/defa/units/model-unit';
import UnitContainer      from 'scripts/defa/units/view-unit-container';
import UnitTechnical      from 'scripts/defa/unit-technicals/model-unit-technical';
import UserContainer      from 'scripts/defa/users/view-user-container';
import MasterControl      from 'scripts/defa/unit-feature-sets/endpoints/homeAutomation/model-master-control';
import PowerConfiguration from 'scripts/defa/unit-feature-sets/endpoints/homeAutomation/model-power-configuration';
import Forecasts          from 'scripts/defa/weather/collection-forecasts';
import Controller         from 'scripts/poppet/controller';
import UnitUserAccess     from 'scripts/defa/access-handler/unit-user-access';

const channelLayout = Bb.Radio.channel( 'layout' ),
	channelNav    = Bb.Radio.channel( 'nav' ),
	channelUser   = Bb.Radio.channel( 'user' ),
	channel       = Bb.Radio.channel( 'objects' );

const subViewMapping = {
	alarmSettings: 'control:alarmSettings',
	accessories  : 'control:accessories',
	calendar     : 'control:calendar',
	dayAndNight  : 'control:dayAndNight',
	details      : 'control:details',
	finder       : 'control:finder',
	gpai         : 'control:gpai',
	map          : 'control:map',
	notifications: 'control:notifications',
	pickupTimes  : 'control:pickupTimes',
	security     : 'control:security',
	settings     : 'control:settings',
	technical    : 'control:technical',
	zones        : 'control:zones',
	weather      : 'control:weather',
};

/**
 * defa.routers.UnitController
 * The Controller for the UnitRouter, all front end urls starting with /devices/.
 * @version 1.2.7
 */
const UnitController = Controller.extend( {
	
	// ------------------------------------------------
	// Routes
	// ------------------------------------------------
	
	/**
	 * Triggers native bluetooth scan, or directs non-native users to serial number.
	 * @void
	 */
	showUnitBluetooth: function () {
		if ( channelLayout.request( 'is:native' ) ) {
			// set url silently to home
			channelNav.trigger( 'app:home', false, {} );
			
			// trigger correct device
			if ( defa.vars.android ) {
				window.android.scanForBleUnits( '' );
			}
			
			if ( defa.vars.webkit ) {
				window.webkit.messageHandlers.scanForBleUnits.postMessage( {} );
			}
			
		} else {
			channelNav.trigger( 'unit:search:serial' );
		}
	},
	
	/**
	 * Show native choice between bluetooth and search.
	 * @void
	 */
	showUnitNativeChoice: function () {
		if ( !channelLayout.request( 'is:native' ) ) {
			channelNav.trigger( 'unit:search:serial', false, { trigger: true, replace: true, } );
		} else {
			
			this.attachUnitContainer( {
				view     : 'unit:nativeChoice',
				noLogin  : true,
				closeLink: false,
			} );
		}
	},
	
	/**
	 * Show the unit search field.
	 * @void
	 */
	showUnitSearch: function () {
		
		this.attachUnitContainer( {
			view     : 'unit:search:serial',
			unit     : new Unit(),
			help     : true, // shows the helptext link
			noLogin  : true,
			closeLink: false,
		} );
	},
	
	/**
	 * Show the Unit help page.
	 * @void
	 */
	showUnitHelp: function () {
		this.attachUnitContainer( {
			view   : 'unit:search:help',
			noLogin: true,
		} );
	},
	
	/**
	 * Shows a list of installer units.
	 * @void
	 */
	showUnitsInstalled: function ( installer, page ) {
		
		this.attachUnitContainer( {
			view     : 'unit:installed',
			installer: installer,
			page     : ( page ) ? ( parseInt( page ) - 1 ) : false,
			closeLink: {
				event: 'app:root',
			},
		} );
	},
	
	/**
	 * Shows a list of all Units.
	 * @void
	 */
	showUnitList: function () {
		
		let units = channelUser.request( 'units' );
		
		// allow read-view for people with 1 or more Bluetooth units even without login.
		this.attachUnitContainer( {
			view   : 'unit:list',
			units  : units,
			noLogin: true,
		} );
	},
	
	/**
	 * Show the bluetooth delete unit panel.
	 * @void
	 */
	showUnitDelete: function ( id ) {
		this.requireUnit( {
			unitId   : id,
			callback : this.attachUnitContainer,
			view     : 'unit:delete',
			subView  : false,
			noLogin  : true,
			closeLink: {
				event : 'back',
			},
		} );
	},
	
	/**
	 * Shows the unit details-interface
	 * @void
	 */
	showUnitDetails: function ( id ) {
		
		this.requireUnit( {
			view     : 'unit:details',
			unitId   : id,
			callback : this.attachUnitContainer,
			noLogin: true,
			closeLink: {
				event : 'unit:control:overview',
				params: [ id, ],
			},
		} );
	},
	
	/**
	 * Shows the unit test-interface.
	 * @void
	 */
	showUnitTest: function ( id ) {
		let self = this;
		
		// create/fetch a unit to attach events to.
		let unit = channel.request( 'units' ).add( new Unit( { id: id, } ), { merge: true } );
		
		// the require-unit will complete
		self.requireUnit( {
			view     : 'unit:test',
			callback : self.attachUnitContainer,
			complete : true,
			noLogin  : true,
			sync     : false,
			unitId   : id,
			closeLink: {
				event : 'unit:control:overview',
				params: [ id, ],
			},
		} );
	},
	
	/**
	 * Shows the control unit interface.
	 * @void
	 */
	showUnitControl: function ( id, sub, subId ) {
		let user = channelUser.request( 'model' );
		
		let closeLink = {
			event : 'unit:control:overview',
			params: [ id, ],
		};
		
		let subView = subViewMapping[ sub ];
		if ( !subView ) {
			subView   = 'control:overview';
			closeLink = {
				event: 'unit:list',
			};
		}
		
		let self = this;
		
		this.requireUnit( {
			unitId   : id,
			self     : self,
			callback : function(options){
				self.attachUnitContainer(options);
				if(UnitUserAccess.hasUnitControlAccess(options.unit, user)){
					self.requireNonCommunicationModels(options);
					self.requireControlModelsAndAttachUnitContainer(options);
				}
			},
			view     : 'unit:control',
			subView  : subView,
			subId    : subId,
			closeLink: closeLink,
			noLogin  : true,
			complete : true,
			sync     : false
		} );
	},
	
	/**
	 * Landing route for : subscriptions/:id/changeunit(/)
	 * @param {Number} id  The id of the subscription to change unit within.
	 * @void
	 */
	showUnitComplaint: function ( id ) {
		
		this.requireUnit( {
			view     : 'unit:complaint',
			subView  : false,
			unitId   : id,
			sync     : false,
			callback : this.attachUnitContainer,
			closeLink: {
				event : 'unit:control:overview',
				params: [ id, ],
			},
		} );
	},
	
	/**
	 * Show the reset "are you sure" view.
	 * @void
	 */
	showUnitReset: function ( id ) {
		
		this.requireUnit( {
			unitId   : id,
			callback : this.attachUnitContainer,
			view     : 'unit:reset',
			subView  : false,
			closeLink: {
				event : 'unit:control:overview',
				params: [ id, ],
			}
		} );
	},
	
	/**
	 * Displays a static view of all the test results available for a unit.
	 * @param {int} id The unit's ID
	 * @void
	 */
	showUnitTestResults: function ( id ) {
		
		let self = this;
		let user = channelUser.request( 'model' );
		
		// if user isn't logged in, do a mini-login.
		if ( channelUser.request( 'is:logged:out' ) ) {
			
			let userContainer = new UserContainer( {
				view      : 'user:login',
				user      : user,
				navHandler: 'internal',
			} );
			
			// FIXME TODO userLoginForm is not defined
			/* global userLoginForm */
			self.listenToOnce( user, 'sync', function () {
				// setup closing animation into test results.
				self.listenTo( userLoginForm, 'animateOut', function () {
					self.showUnitTestResults( id );
				} );
				
				// remove closelink, and animate out
				userLoginForm.closeLink = false;
				userLoginForm.animateOut();
			} );
			
			channelLayout.request( 'root' ).showChildView( 'main', userContainer );
			
			// if user is logged in, show the test results.
		} else {
			this.requireUnit( {
				view     : 'unit:testresults',
				unitId   : id,
				complete : true,
				callback : this.attachUnitContainer,
				sync     : false,
				closeLink: {
					event: 'unit:list',
				},
			} );
		}
	},
	
	/**
	 * Shows the unit alias-interface
	 * @void
	 */
	showUnitAlias: function ( id ) {
		
		this.requireUnit( {
			view     : 'unit:alias',
			unitId   : id,
			callback : this.attachUnitContainer,
			closeLink: {
				event : 'unit:control:overview',
				params: [ id, ],
			},
		} );
	},
	
	/**
	 * Shows the unit alias-interface
	 * @void
	 */
	showUnitAlarm: function ( id ) {
		
		this.requireUnit( {
			view     : 'unit:alarm',
			unitId   : id,
			callback : this.attachUnitContainer,
			closeLink: {
				event : 'unit:control:overview',
				params: [ id, ],
			},
		} );
	},
	
	/**
	 * Shows the unit notes-interface
	 * @void
	 */
	showUnitNotes: function ( id ) {
		
		this.requireUnit( {
			view     : 'unit:notes',
			unitId   : id,
			callback : this.attachUnitContainer,
			closeLink: {
				event : 'unit:control:overview',
				params: [ id, ],
			},
		} );
	},
	
	/**
	 * Shows the unit location-interface
	 * @void
	 */
	showUnitLocation: function ( id ) {
		
		this.requireUnit( {
			view     : 'unit:location',
			unitId   : id,
			callback : this.attachUnitContainer,
			closeLink: {
				event : 'unit:control:overview',
				params: [ id, ],
			},
		} );
	},
	

	/**
	 * Shows the unit vehicle-interface
	 * @void
	 */
	showUnitVehicle        : function ( id ) {
		
		this.requireUnit( {
			view     : 'unit:vehicle',
			unitId   : id,
			complete : true,
			callback : this._showSettingsAutomotive,
			closeLink: {
				event : 'unit:control:settings',
				params: [ id, ],
			},
		} );
	},
	_showSettingsAutomotive: function ( options ) {
		
		let self = options.self || this;
		
		let unitId               = options.unit.get( 'id' ),
			settingsAutomotive   = false,
			settingsAutomotives  = channel.request( 'settingsAutomotives' ),
			settingsAutomotiveId = options.unit.get( 'transientSettingsAutomotive' );
		
		// since the unit is required, the settingsAutomotive is radio available if it exists. (NOTE: Corrupted data
		// may screw this up...)
		if ( settingsAutomotiveId ) {
			settingsAutomotive = settingsAutomotives.get( settingsAutomotiveId );
		} else {
			settingsAutomotive = new SettingsAutomotive( { unit: unitId } );
		}
		
		options.settingsAutomotive = settingsAutomotive;
		
		// log the current options before sending to layout:
		self.attachUnitContainer( options );
		
	},
	
	/**
	 * Shows the unit install-interface
	 * @void
	 */
	showUnitInstall: function ( id, context, email ) {
		
		// context is a user state/requested view in the claim flow, so prefix it with "user".
		context = 'user:' + ( context || 'login' );
		email   = email || '';
		
		let self         = this,
			subscription = new Subscription();
		
		// add subscription listeners here, so they are active even when the container gets destroyed.
		self.listenToOnce( subscription, 'request', self.onSubscriptionRequest );
		self.listenToOnce( subscription, 'sync', self.onSubscriptionSync );
		self.listenToOnce( subscription, 'error', self.onSubscriptionError );
		
		self.requireUnit( {
			subscription: subscription,
			view        : 'unit:install',
			email       : email,
			context     : context,
			unitId      : id,
			complete    : true,
			sync        : false,
			noLogin     : true,
			closeLink   : {
				event : 'unit:control:overview',
				params: [ id, ],
			},
			callback    : self.attachUnitContainer,
		} );
	},
	
	/**
	 * Fetches a unit with a token and displays its install-view.
	 * @param {int}    id     The unit's ID.
	 * @param {String} token  The access token to unlock the unit by.
	 * @void
	 */
	showUnitWithToken: function ( id, context, email, token ) {
		
		// check if you already got the unit saved:
		if ( channel.request( 'units' ).get( id ) ) {
			channelNav.trigger( 'unit:list' );
			
			// the unit is new, start the token install flow.
		} else {
			
			context = 'user:' + ( context || 'create' );
			
			let unit  = new Unit();
			email = email || '';
			
			this.listenToOnce( unit, 'error', function ( model, response ) {
				
				// if no token is needed to open the unit, just go to the normal install flow
				if ( response.responseJSON.transientNoToken ) {
					channelNav.trigger( 'unit:install', [ id, ] );
					
					// display an error
				} else {
					channelLayout.trigger( 'error', response.responseJSON );
				}
			} );
			
			this.listenToOnce( unit, 'sync', function () {
				this.stopListening( unit, 'error' );
				// add the new unit to the channel.
				unit = channel.request( 'units' ).add( unit, { merge: true } );
				
				// get the subscription if it exists:
				let subscription = ( unit.get( 'transientSubscription' ) )
								   ? channel.request( 'subscriptions' ).get( unit.get( 'transientSubscription' ) )
								   : new Subscription();
				
				this.listenToOnce( subscription, 'request', this.onSubscriptionRequest );
				this.listenToOnce( subscription, 'sync', this.onSubscriptionSync );
				
				// attach unit layout
				this.attachUnitContainer( {
					subscription: subscription,
					view        : 'unit:install',
					email       : email,
					context     : context,
					unitId      : id,
					unit        : unit,
					token       : token,
					sync        : false,
					noLogin     : true,
					closeLink   : {
						event : 'unit:control:overview',
						params: [ id, ],
					},
				} );
			} );
			
			// fetch unit with token.
			unit.fetch( {
				data       : {
					id   : id,
					token: token,
				},
				processData: true,
			} );
		}
		
	},
	
	
	// ------------------------------------------------
	// Event handlers
	// ------------------------------------------------
	
	// - model event handlers
	
	/**
	 * Triggers when a unit used by this controller syncs.
	 * @void
	 */
	onUnitSync: function ( model ) {
		this.unitContainer.animateChildOut( {
			closeLink: {
				event : 'unit:control:overview',
				params: [ model.get( 'id' ), ],
			},
		} );
	},
	
	// - external event handlers
	
	/**
	 * Temp hack to avoid the new native channelNav, as it is NOT ready.
	 * @void
	 */
	onUnitContainerControl: function ( id ) {
		this.showUnitControl( id );
	},
	
	/**
	 * Triggers when the UnitContainer asks to update a unit.
	 * @void
	 */
	onUnitContainerUpgrade: function ( id ) {
		if ( channelLayout.request( 'is:native' ) ) {
			
			if ( defa.vars.android ) {
				window.android.upgradeBleDevice( id );
			}
			
			if ( defa.vars.webkit ) {
				window.webkit.messageHandlers.upgradeBleDevice.postMessage( id );
			}
		}
	},
	
	// - subscription event handlers
	
	/**
	 * Triggers when a new subscription is requested to be saved.
	 * @void
	 */
	onSubscriptionRequest: function ( model ) {
		let configView = new ConfigView();
		channelLayout.request( 'root' ).showChildView( 'main', configView );
	},
	
	/**
	 * Triggers when a new subscription is created.
	 * @void
	 */
	onSubscriptionSync: function ( model ) {
		// setup vars
		let unit = channel.request( 'units' ).get( model.get( 'unit' ) ),
			user = channelUser.request( 'model' );
		
		// set the configs for the finished Unit.
		unit.set( 'transientSubscription', model.get( 'id' ) );
		
		// add the Unit to the User's transientUnits.
		user.get( 'transientUnits' ).push( model.get( 'unit' ) );
		
		// pushing to an array does not trigger change events, so we make a manual exception here.
		user.trigger( 'change:transientUnits', user );
		
		// if the end user haven't moved from config, show the done view.
		if ( channelLayout.request( 'root' ).getChildView( 'main' ) instanceof ConfigView ) {
			
			let successView = new SuccessView( {
				model    : model,
				context  : 'subscription:create',
				closeLink: {
					event : 'unit:control:overview',
					params: [ model.get( 'unit' ), ],
				},
			} );
			
			this.listenTo( successView, 'success:close', function () {
				successView.animateOut();
			} );
			
			channelLayout.request( 'root' ).showChildView( 'main', successView );
		}
	},
	
	/**
	 * Triggers when a new subscription fails to be saved.
	 * @void
	 */
	onSubscriptionError: function ( model ) {
		channelLayout.trigger( 'error', { model: model } );
	},
	
	// - unit container event handlers
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	/**
	 * Requests a Unit, before calling the callback.
	 * @param {Object} options All the needed options, most importantly a unitId and a callback function.
	 * @void
	 */
	requireUnit: function ( options ) {
		this.requireModel( 'Unit', defa.models, options );
	},
	
	
	/**
	 * Prepare Control Models for Unit Control View:
	 * 1. Require endpoints (feature set) for the unit
	 * 2. Require needed control models
	 * 3. Attach control unit container
	 * @param {Object} options All the needed options, most importantly a unitId,
	 *                         which will be used as id for all belonging Control Models
	 * @void
	 */
	requireControlModelsAndAttachUnitContainer: function( options ) {
		let id = options.unit.get( 'id' );
		let self = options.self || this;
		
		
		
		self.requireModel('UnitFeatureSet', defa.models, {
			self: self,
			unitOptions: options,
			unitFeatureSetId: id,
			complete: false,
			continue: true,
			callback: function( options ) {

				options.unitOptions.featureSet = options.unitFeatureSet;
				self.stopListening(options.unitOptions.featureSet, 'refresh');
				self.listenTo( options.unitOptions.featureSet, 'refresh', self.onFeatureSetRefresh);
				if(options.failed) {
					options.unitOptions.featureSetFailed = true;
					options.unitOptions.featureSetResponse = options.responseJSON;
				} else {
					options.unitOptions.featureSetFailed = false;
					self.requireControlModels(options.unitOptions, id);
				}
				options.self.attachUnitContainer(options.unitOptions);
			}
		});
	},
	
	onFeatureSetRefresh: function(view){
		let options = view.options;
		this.requireControlModelsAndAttachUnitContainer(options);
	},
	/**
	 * Prepare Models for Unit Control View:
	 * 1. Require models that does not communicate with the unit (does not need feature set)
	 * @param {Object} options All the needed options
	 * @void
	 */
	requireNonCommunicationModels: function(options){
		let self = options.self || this;
		
		
		let location = null;
		if(options.unit.get( 'transientSettingsHome' )){
			location = channel.request( 'settingsHomes' ).get( options.unit.get( 'transientSettingsHome' ) ).get( 'location' );
		}
		//Only fetch weather if a location has been set
		if ( location !== null ) {
			let forecasts          = new Forecasts();
			forecasts.location     = location;
			forecasts.on( 'parse:complete', function (collection) {
				options.forecasts = collection
			} );
			forecasts.fetch();
		}
		
		let unitId = options.unitId;
		const defaultModel = { id: unitId, useBackendApi: true };
		defaultModel.getUnread          = true;
		options.notificationHolderModel = channel.request( 'notificationHolders' ).add( defaultModel, { merge: true } );
		this.requireModel( 'NotificationHolder', defa.models, {
			self: self,
			unitOptions: options,
			notificationHolderId: unitId,
			complete            : true,
			continue            : true,
			callback            : function ( options ) {
				console.log( 'NotificationHolder model required' );
				options.self.attachUnitContainer(options.unitOptions);
				// move this
			}
		} );
	},
	
	/**
	 * Require Control Models.
	 * This function inserts the needed control models into options, and fetches them if needed.
	 *
	 * NB! If the model needs to be fetched, it will happen async to this function, in the following manner:
	 * 1. Model object will be inserted into options
	 * 2. Model fetch will start
	 * 3. requireControlModels returns
	 * 4. Model is fetched and the model object in options will be updated and emit a 'change' event.
	 *
	 * @param {Object} options All the needed options, most importantly a unitId and featureSet. The models will
	 *                         be added to this object.
	 * @void
	 */
	requireControlModels: function ( options, id ) {
		console.log('requireControlModels');
		let featureSet = options.featureSet;
		
		if(!featureSet) {
			console.warn('FeatureSet not found!');
			return;
		}


		const defaultModel = {id: id, useBackendApi: featureSet.useBackendApi() };
		
		if(featureSet.hasWarmUp()) {
			options.pickupTimeModel = channel.request( 'pickupTimes' ).add( defaultModel, { merge: true } );
			options.warmupStateModel = channel.request( 'warmupStates' ).add( defaultModel, { merge: true } );
			options.warmupSettingModel = channel.request( 'warmupSettings' ).add( defaultModel, { merge: true } );

			let warmupModel = channel.request( 'warmups' ).add( defaultModel, {merge: true} );
			this.requireModel('Warmup', defa.models, {
				continue: true,
				warmupId: id,
				complete: true,
				callback: function( options ) {
					console.log('Warmup endpoint model required');
				}
			})
		}
		
		if(featureSet.hasSecurity()){
			options.securityModel = channel.request('securitys').add( { id: id }, { merge: true });
			this.requireModel('Security', defa.models, {
				continue: true,
				securityId: id,
				complete: true,
				callback: function(options){
					console.log('Security model required');
				}
			});
		}
		
		if(featureSet.hasFinder()) {
			options.finderModel = channel.request( 'finders' ).add( { id: id } , { merge: true } );
			this.requireModel('Finder', defa.models, {
				continue: true,
				finderId: id,
				complete: true,
				callback: function( options ) {
					console.log('Finder model required');
				}
			})
		}
		
		if (featureSet.hasGenericStates()) {
			options.genericStateModel = channel.request( 'genericStates' ).add( defaultModel, { merge: true } );
			this.requireModel('GenericState', defa.models,{
				continue: true,
				genericStateId: id,
				complete: true,
				callback: function ( options ) {
					console.log( 'GenericState model required' );
				}
			})
		}

		if (featureSet.hasMasterControl()) {
			options.alarmSwitchModel = channel.request( 'alarmSwitchs' ).add( defaultModel, { merge: true } );
			options.heatingSwitchModel = channel.request( 'heatingSwitchs' ).add( defaultModel, { merge: true } );
			options.calendarModel = channel.request( 'calendars' ).add( defaultModel, { merge: true } );
			options.temperatureAlertModel = channel.request( 'temperatureAlerts' ).add( defaultModel, { merge: true } );
			options.dayAndNightModel = channel.request( 'dayAndNightSettings' ).add( defaultModel, {merge: true} );

			new MasterControl(defaultModel).fetch();
		}

		if (featureSet.hasWiredAlarmInput()) {
			options.followMainAlarmSwitchModel = channel.request( 'followMainAlarms' ).add( defaultModel, { merge: true } );
			options.generalAlarmInputSwitchModel = channel.request( 'generalAlarmInputs' ).add( defaultModel, { merge: true } );

			new IasZone(defaultModel).fetch();
		}

		if (featureSet.hasZones()) {
			let defaultZoneHolderModel = defaultModel;
			defaultZoneHolderModel.zoneIds = featureSet.getZoneIds();
			options.zoneHolderModel = channel.request( 'zoneHolders' ).add( defaultZoneHolderModel, { merge: true } );
			this.requireModel('ZoneHolder', defa.models,{
				continue: true,
				zoneHolderId: id,
				complete: true,
				callback: function ( options ) {
					console.log( 'ZoneHolder model required' );
				}
			})
		}
		
		if (featureSet.hasDigiHeat()) {
			options.digiHeatModel = channel.request( 'digiHeatModes' ).add( defaultModel, { merge: true } );
			this.requireModel('DigiHeatMode', defa.models,{
				continue: true,
				digiHeatModeId: id,
				complete: true,
				callback: function ( options ) {
					console.log( 'DigiHeatMode model required' );
				}
			})
		}

		if (featureSet.hasAccessories()) {
			options.accessoriesHolderModel = channel.request( 'accessoriesHolders' ).add( defaultModel, { merge: true } );
			this.requireModel('AccessoriesHolder', defa.models,{
				continue: true,
				accessoriesHolderId: id,
				complete: true,
				callback: function ( options ) {
					console.log( 'AccessoriesHolder model required' );
				}
			})
		}

		if (featureSet.hasPowerConfiguration()) {
			options.batteryAlertModel = channel.request( 'batteryAlerts' ).add( defaultModel, { merge: true } );
			options.powerAlertModel = channel.request( 'powerAlerts' ).add( defaultModel, { merge: true } );
			
			new PowerConfiguration(defaultModel).fetch();
		}

		/*TODO Add checks for all other features, following this pattern:
		* In this example, the feature is named 'fooFeature' (collection is named 'fooFeatures')
		*
		* if(featureSet.hasFooFeature()) {
		* 	options.featureModel = channel.request( 'fooFeatures' ).add( { id: id } , { merge: true } );
		* 	this.requireModel('FooFeatures', defa.models, {
		* 		fooFeatureId: id,
		* 		complete: true,
		* 		callback: function( options ) {
		* 			console.log('FooFeatures model required');
		* 		}
		* 	})
		* }
		*/
		
	},
	
	/**
	 * Create and display a UnitContainer.
	 * @param {Object} options  An object containing all the needed objects for creating, listing and editing
	 *     Unit-information.
	 * @void
	 */
	attachUnitContainer: function ( options ) {
		
		let user = channelUser.request( 'model' );
		
		options         = options || {};
		options.view    = options.view || 'create';
		options.user    = options.user || user;
		options.noLogin = options.noLogin || false;
		
		// if there is a bluetooth unit, pass noLogin flag.
		if ( options.unit && ( options.unit instanceof Bb.Model ) && 'BLUETOOTH' === options.unit.get( 'family' ) ) {
			options.noLogin = true;
		}
		
		// if user isn't logged in, and this view isn't a no-login view, route to login.
		if ( channelUser.request( 'is:logged:out' ) && !options.noLogin ) {
			channelNav.trigger( 'user:login', false, { trigger: true, replace: true, } );
			
			// if the user is logged in, play with the unitContainer.
		} else {
			
			let self = options.self || this;
			
			// if the UnitContainer isn't made or is destroyed, create it and attach it's listeners
			if ( !( self.unitContainer instanceof UnitContainer ) || self.unitContainer.isDestroyed() ) {
				
				self.unitContainer = new UnitContainer( options );
				
				self.listenTo( self.unitContainer, 'destroy', function () {
					self.stopListening( self.unitContainer );
				} );
				
				self.listenTo( self.unitContainer, 'unit:control', self.onUnitContainerControl );
				self.listenTo( self.unitContainer, 'upgrade', self.onUnitContainerUpgrade );
				
				// if it already exists, just update it's options
			} else {
				self.unitContainer.settings( options );
			}
			
			// if sync is true (default) create a listener for the unit.
			if ( options.unit ) {
				self.stopListening( options.unit, 'sync', self.onUnitSync );
				
				if ( !_.isBoolean( options.sync ) || options.sync ) {
					self.listenTo( options.unit, 'sync', self.onUnitSync );
				}
			}
			
			// if the container isn't attached yet, attach it.
			if ( !self.unitContainer.isAttached() ) {
				channelLayout.request( 'root' ).showChildView( 'main', self.unitContainer );
			}
		}
	},
	
} );


export default UnitController;

